/*
 * THE IN-APP CONFIRM / PROMPT HOST — renders whatever confirm.ts has queued,
 * on the app's own .overlay + .modal. Mounted once at the app root (App.tsx).
 * See src/confirm.ts for the imperative API that feeds it.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useConfirmStore } from '../confirm';
import { useDismiss, useFocusTrap } from '../motion';

export default function ConfirmHost() {
  const current = useConfirmStore((s) => s.current);
  // Keyed by request id so each one is a fresh dialog — its own field value, and
  // a clean focus-trap capture of whoever opened it.
  return current ? <Dialog key={current.id} /> : null;
}

function Dialog() {
  const req = useConfirmStore((s) => s.current)!;
  const resolveCurrent = useConfirmStore((s) => s.resolveCurrent);
  const closeCurrent = useConfirmStore((s) => s.closeCurrent);
  const panelRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(req.kind === 'prompt' ? req.initial ?? '' : '');

  // The safe result if the person backs out: false for a confirm, null (not "")
  // for a prompt, so a cancelled prompt is distinguishable from an empty one.
  const cancelResult: boolean | null = req.kind === 'prompt' ? null : false;
  // The animation only removes the dialog from screen now — the result is
  // resolved the instant the choice is made (in `finish`), not when the leave
  // finishes, so there is no window where a confirmed action has not yet run.
  const { leaving, dismiss } = useDismiss(() => closeCurrent());
  // Latch: the FIRST choice wins. A stray event during the ~240ms leave (a
  // backdrop click, an Escape, a focus-out) must not re-resolve or flip it.
  const done = useRef(false);
  const finish = (r: boolean | string | null) => {
    if (done.current) return;
    done.current = true;
    resolveCurrent(r); // resolve the awaiting caller immediately…
    dismiss(); // …and animate out; `closeCurrent` removes it after.
  };

  useFocusTrap(panelRef, true);

  /*
   * This dialog is the topmost layer, so it owns the keyboard outright while
   * open — a capture-phase listener with stopImmediatePropagation means a card
   * or form beneath never also reacts to the same Escape. (This is also why the
   * Safari window.confirm flash is gone: there is no native dialog to swallow.)
   *
   * Enter is the default action for a PROMPT and a CAUTIONARY confirm only.
   * A DESTRUCTIVE confirm binds no default — Return must never delete by reflex;
   * the person has to click. Space still activates whatever button has focus.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        finish(cancelResult);
        return;
      }
      if (e.key === 'Enter') {
        const enterConfirms =
          req.kind === 'prompt' || (req.kind === 'confirm' && req.tone === 'caution');
        if (!enterConfirms) return;
        if (req.kind === 'prompt') {
          if (req.required && !value.trim()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          finish(value);
        } else {
          e.preventDefault();
          e.stopImmediatePropagation();
          finish(true);
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // value feeds the Enter guard for a required prompt.
  }, [req, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelLabel = req.cancelLabel ?? 'Cancel';

  // Portalled to <body> so it always paints over everything it is asked about —
  // a card, a wizard, or a dialog that portals itself (ScopeAdjust, Correction).
  // See .overlay-confirm in styles.css for the fixed position + z-index.
  return createPortal(
    <div
      className={`overlay overlay-confirm ${leaving ? 'is-leaving' : ''}`}
      onClick={() => finish(cancelResult)}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`modal confirm ${leaving ? 'is-leaving' : ''}`}
        role={req.kind === 'prompt' ? 'dialog' : 'alertdialog'}
        aria-modal="true"
        aria-label={req.title}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="confirm-title">{req.title}</p>
        {req.body != null && <div className="confirm-body">{req.body}</div>}

        {req.kind === 'prompt' && req.current && (
          <label className="confirm-field">
            <span className="confirm-flab">{req.current.label}</span>
            <input className="input" value={req.current.value} disabled readOnly />
          </label>
        )}

        {req.kind === 'prompt' && (
          <label className="confirm-field">
            <span className="confirm-flab">
              {req.label}
              {req.required && (
                <span className="req" aria-hidden="true">
                  *
                </span>
              )}
            </span>
            <input
              className="input"
              autoFocus
              value={value}
              placeholder={req.placeholder}
              aria-required={req.required || undefined}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
        )}

        <div className="confirm-btns">
          {/* An acknowledge-only alert (a hard block the person cannot override)
              shows no quiet button — there must be no way through. Other dialogs
              keep their cancel. */}
          {!(req.kind === 'confirm' && req.alert) && (
            <button
              type="button"
              className="btn"
              autoFocus={req.kind === 'confirm'}
              onClick={() => finish(cancelResult)}
            >
              {cancelLabel}
            </button>
          )}
          {req.kind === 'confirm' ? (
            <button
              type="button"
              className={`btn ${req.tone === 'danger' ? 'btn-danger-solid' : 'btn-primary'}`}
              onClick={() => finish(true)}
            >
              {req.confirmLabel}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!!req.required && !value.trim()}
              onClick={() => finish(value)}
            >
              {req.submitLabel ?? 'Confirm'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
